import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';

export interface ChartSeries { key: string; label: string; color: string; }

@Component({
  selector: 'app-chart',
  standalone: false,
  template: `<div class="chart-canvas" [style.height.px]="height">
    <canvas #canvas tabindex="0" aria-label="Chart. Use left and right arrow keys to inspect values."
      (pointermove)="showPointer($event)" (pointerleave)="hideTooltip()" (blur)="hideTooltip()"
      (keydown)="showKeyboard($event)"></canvas>
    <div *ngIf="tooltip" class="chart-tooltip" role="tooltip" [style.left.px]="tooltip.left" [style.top.px]="tooltip.top">
      <strong>{{ tooltip.title }}</strong>
      <div *ngFor="let item of tooltip.values"><span [style.color]="item.color">●</span> {{ item.label }}: <b>{{ item.value }}</b></div>
    </div>
  </div>`,
  styles: [`.chart-canvas { position: relative; }
    .chart-tooltip { position: absolute; z-index: 5; pointer-events: none; width: 210px; max-width: calc(100% - 16px); padding: 10px 12px; background: #fff; color: #34354f; border: 1px solid #d8cce6; border-radius: 8px; box-shadow: 0 4px 16px #34354f26; font-size: 12px; line-height: 1.6; }
    .chart-tooltip strong { display: block; color: #60259e; margin-bottom: 4px; }
  `],
})
export class ChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() type: 'line' | 'bar' = 'line';
  @Input() rows: object[] = [];
  @Input() xKey = 'Month';
  @Input() series: ChartSeries[] = [];
  @Input() showLegend = true;
  @Input() height = 300;
  @ViewChild('canvas') canvas?: ElementRef<HTMLCanvasElement>;
  private observer?: ResizeObserver;
  private selectedIndex = -1;
  tooltip: {title: string; left: number; top: number; values: Array<{label: string; color: string; value: string}>} | null = null;

  hideTooltip(): void { this.tooltip = null; this.selectedIndex = -1; }

  private formatMonth(value: unknown): string {
    const raw = String(value ?? '');
    const match = /^(\d{4})-(\d{2})$/.exec(raw);
    if (!match) return raw;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
    return new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric' }).format(date);
  }

  showPointer(event: PointerEvent): void {
    const rect = this.canvas!.nativeElement.getBoundingClientRect();
    const width = Math.max(this.canvas!.nativeElement.parentElement!.clientWidth, 300);
    const x = (event.clientX - rect.left) * width / rect.width;
    const y = (event.clientY - rect.top) * this.height / rect.height;
    if (!this.rows.length || x < 55 || x > width - 16 || y < 35 || y > this.height - 65) { this.hideTooltip(); return; }
    const index = Math.min(this.rows.length - 1, Math.floor((x - 55) / ((width - 71) / this.rows.length)));
    if (this.type === 'bar') {
      const step = (width - 71) / this.rows.length;
      const barWidth = Math.max(1, Math.min(25, (step - 8) / Math.max(this.series.length, 1)));
      const firstBarX = 55 + step * (index + 0.5) - barWidth * this.series.length / 2;
      const seriesIndex = Math.floor((x - firstBarX) / barWidth);
      if (seriesIndex < 0 || seriesIndex >= this.series.length) { this.hideTooltip(); return; }
      this.showValue(index, event.clientX - rect.left, event.clientY - rect.top, seriesIndex);
      return;
    }
    this.showValue(index, event.clientX - rect.left, event.clientY - rect.top);
  }

  showKeyboard(event: KeyboardEvent): void {
    if (!this.rows.length || !['ArrowLeft', 'ArrowRight', 'Escape'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Escape') { this.hideTooltip(); return; }
    const index = this.selectedIndex < 0 ? 0 : Math.max(0, Math.min(this.rows.length - 1, this.selectedIndex + (event.key === 'ArrowLeft' ? -1 : 1)));
    this.showValue(index, 55, 35);
  }

  private showValue(index: number, x: number, y: number, seriesIndex?: number): void {
    const row = this.rows[index] as Record<string, unknown>;
    this.selectedIndex = index;
    const width = this.canvas!.nativeElement.getBoundingClientRect().width;
    const visibleSeries = seriesIndex == null ? this.series : [this.series[seriesIndex]];
    this.tooltip = {
      title: this.formatMonth(row[this.xKey]),
      left: Math.max(8, Math.min(x + 12, width - 226)),
      top: Math.max(8, Math.min(y + 12, this.height - 50 - visibleSeries.length * 20)),
      values: visibleSeries.map(item => ({label: item.label, color: item.color, value: row[item.key] == null ? 'NIL' : String(row[item.key])})),
    };
  }

  ngAfterViewInit(): void {
    this.observer = new ResizeObserver(() => this.render());
    this.observer.observe(this.canvas!.nativeElement.parentElement!);
    this.render();
  }
  ngOnChanges(_changes: SimpleChanges): void { if (this.canvas) this.render(); }
  ngOnDestroy(): void { this.observer?.disconnect(); }

  private render(): void {
    this.hideTooltip();
    const canvas = this.canvas?.nativeElement;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const width = Math.max(canvas.parentElement?.clientWidth ?? 0, 300);
    const height = this.height;
    const pixelRatio = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, width, height);
    const left = 55, right = 16, top = 35, bottom = 65;
    const plotWidth = width - left - right, plotHeight = height - top - bottom;
    const value = (row: object, key: string) => Number((row as Record<string, unknown>)[key] ?? 0) || 0;
    let maximum = 1;
    this.rows.forEach(row => this.series.forEach(item => { maximum = Math.max(maximum, value(row, item.key)); }));
    const ceiling = Math.ceil(maximum * 1.1);
    context.font = '12px Segoe UI, sans-serif';
    context.textBaseline = 'middle';
    for (let tick = 0; tick <= 4; tick++) {
      const y = top + plotHeight * tick / 4;
      context.strokeStyle = '#e8e4ed';
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(left, y);
      context.lineTo(width - right, y);
      context.stroke();
      context.fillStyle = '#626176';
      context.textAlign = 'right';
      context.fillText(String(Math.round(ceiling * (4 - tick) / 4)), left - 8, y);
    }
    const count = this.rows.length, step = plotWidth / Math.max(count, 1);
    const labelEvery = this.type === 'bar' ? 1 : Math.max(1, Math.ceil(count / Math.max(1, Math.floor(plotWidth / 75))));
    for (let index = 0; index < count; index++) {
      if (index % labelEvery !== 0) continue;
      const label = this.formatMonth((this.rows[index] as Record<string, unknown>)[this.xKey]);
      context.save();
      context.translate(left + step * (index + 0.5), top + plotHeight + 10);
      context.rotate(-Math.PI / 4);
      context.textAlign = 'right';
      context.fillStyle = '#626176';
      context.fillText(label, 0, 0);
      context.restore();
    }
    this.series.forEach((item, seriesIndex) => {
      context.strokeStyle = item.color;
      context.fillStyle = item.color;
      context.lineWidth = 2;
      if (this.type === 'line') {
        context.beginPath();
        this.rows.forEach((row, index) => {
          const x = left + step * (index + 0.5);
          const y = top + plotHeight * (1 - value(row, item.key) / ceiling);
          if (index === 0) context.moveTo(x, y); else context.lineTo(x, y);
        });
        context.stroke();
      } else {
        const barWidth = Math.max(1, Math.min(25, (step - 8) / Math.max(this.series.length, 1)));
        this.rows.forEach((row, index) => {
          const barHeight = plotHeight * value(row, item.key) / ceiling;
          const x = left + step * (index + 0.5) - barWidth * this.series.length / 2 + seriesIndex * barWidth;
          context.fillRect(x, top + plotHeight - barHeight, barWidth, barHeight);
        });
      }
      if (this.showLegend) {
        const legendX = left + seriesIndex * 160;
        context.fillRect(legendX, 8, 11, 11);
        context.fillStyle = '#34354f';
        context.textAlign = 'left';
        context.fillText(item.label, legendX + 16, 14);
      }
    });
  }
}
